// Walkthrough coach — step card UI + engine hook.
//
// `useWalkthroughEngine` owns the walkthrough state machine (stepIndex,
// completed, action-stream advance). `<WalkthroughCoach>` renders the
// header + step card and is presentation-only — it does NOT mount the
// simulator. The Equipment Lab single-sim view mounts the simulator
// itself and pipes its onAction stream into the engine, then renders
// the coach docked beside the panel at full canvas.
//
// The inline `<GuidedWalkthrough>` (slide drills) keeps its own
// self-contained layout; this module is for the Lab's docked surface.

import { useCallback, useEffect, useMemo, useState } from "react";
import type { WalkthroughStep } from "./help-types";

export interface WalkthroughEngine {
  stepIndex: number;
  completed: boolean;
  step: WalkthroughStep | undefined;
  atEnd: boolean;
  progressPct: number;
  totalSteps: number;
  advance: () => void;
  restart: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleAction: (action: any) => void;
}

export function useWalkthroughEngine(
  steps: WalkthroughStep[],
): WalkthroughEngine {
  const [stepIndex, setStepIndex] = useState(0);
  const [completed, setCompleted] = useState(false);

  const lastIdx = steps.length - 1;
  const step = steps[stepIndex];
  const atEnd = stepIndex === lastIdx;

  const advance = useCallback(() => {
    setStepIndex((i) => {
      if (i < lastIdx) return i + 1;
      setCompleted(true);
      return i;
    });
  }, [lastIdx]);

  const restart = useCallback(() => {
    setStepIndex(0);
    setCompleted(false);
  }, []);

  const handleAction = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (action: any) => {
      if (completed) return;
      if (!step?.matches) return;
      if (step.matches(action)) advance();
    },
    [completed, step, advance],
  );

  const progressPct = useMemo(() => {
    const denom = Math.max(1, steps.length);
    return Math.round(((stepIndex + (completed ? 1 : 0)) / denom) * 100);
  }, [stepIndex, completed, steps.length]);

  useEffect(() => {
    setStepIndex(0);
    setCompleted(false);
  }, [steps]);

  return {
    stepIndex,
    completed,
    step,
    atEnd,
    progressPct,
    totalSteps: steps.length,
    advance,
    restart,
    handleAction,
  };
}

interface WalkthroughCoachProps {
  title: string;
  engine: WalkthroughEngine;
}

export default function WalkthroughCoach({
  title,
  engine,
}: WalkthroughCoachProps) {
  const { stepIndex, completed, step, atEnd, progressPct, totalSteps, advance, restart } =
    engine;

  return (
    <div className="walkthrough-coach" data-archetype="walkthrough-coach">
      <header className="wc-header">
        <span className="wc-eyebrow">Walkthrough</span>
        <h2 className="wc-title">{title}</h2>
        <div className="wc-progress" aria-label="Walkthrough progress">
          <div className="wc-progress-bar" style={{ width: `${progressPct}%` }} />
          <span className="wc-progress-text">
            {completed ? "Done" : `Step ${stepIndex + 1} of ${totalSteps}`}
          </span>
        </div>
      </header>

      <div className="wc-step-card" role="region" aria-live="polite">
        {!completed && step && (
          <>
            <p className="wc-prompt">{step.body}</p>
            <div className="wc-actions">
              <button
                type="button"
                className="wc-btn wc-btn--primary"
                onClick={advance}
              >
                {atEnd ? "Finish walkthrough" : step.matches ? "Skip step →" : "Next →"}
              </button>
            </div>
          </>
        )}
        {completed && (
          <>
            <p className="wc-prompt">
              Walkthrough complete. You've seen every primary control on the panel.
              Free-play continues — try a scenario or recall presets to lock it in.
            </p>
            <div className="wc-actions">
              <button
                type="button"
                className="wc-btn wc-btn--ghost"
                onClick={restart}
              >
                Restart walkthrough
              </button>
            </div>
          </>
        )}
      </div>

      <style>{COACH_STYLES}</style>
    </div>
  );
}

const COACH_STYLES = `
.walkthrough-coach {
  display: flex; flex-direction: column;
  gap: 12px;
  width: 340px;
  flex: 0 0 340px;
  padding: 16px;
  background: #f5efde;
  border: 1px solid #d4cdb8;
  border-radius: 12px;
  box-sizing: border-box;
  align-self: flex-start;
  position: sticky;
  top: 16px;
}
.wc-header { display: flex; flex-direction: column; gap: 6px; }
.wc-eyebrow {
  font-size: 10px; letter-spacing: 2px; font-weight: 700;
  text-transform: uppercase; color: #6e6452;
}
.wc-title { margin: 0; font-size: 18px; line-height: 1.25; color: #2a261d; }
.wc-progress {
  position: relative;
  height: 6px;
  border-radius: 999px;
  background: #e6dfc8;
  overflow: hidden;
  margin-top: 6px;
}
.wc-progress-bar {
  position: absolute; inset: 0 auto 0 0;
  background: #2a261d;
  border-radius: 999px;
  transition: width 200ms ease;
}
.wc-progress-text {
  position: absolute; right: 0; top: 10px;
  font-size: 11px; color: #6e6452;
}
.wc-step-card {
  background: #fffdf5;
  border: 1px solid #d4cdb8;
  border-radius: 8px;
  padding: 14px;
  margin-top: 14px;
}
.wc-prompt {
  margin: 0 0 12px;
  font-size: 14px; line-height: 1.55; color: #2a261d;
}
.wc-actions { display: flex; gap: 8px; }
.wc-btn {
  font-size: 13px; font-weight: 600;
  padding: 8px 14px;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid transparent;
}
.wc-btn--primary {
  background: #2a261d; color: #f5efde; border-color: #2a261d;
}
.wc-btn--primary:hover { background: #3a352b; }
.wc-btn--ghost {
  background: transparent; color: #2a261d; border-color: #d4cdb8;
}
.wc-btn--ghost:hover { background: #ebe4cf; }
`;
